import { useState, useMemo } from "react";
import { AlertTriangle, ArrowRight, Check, Info, Loader2 } from "lucide-react";
import { hasSailWarningReason, sailWarningTooltip } from "../../../lib/provisionalReasons";
import { checkMateArcNotes, fillTemplate, hasArcProvisionalReason, localizeArcLine } from "../../../lib/arcDisplay";
import en from "../../../locales/en";
import cs from "../../../locales/cs";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { FlightModePanel } from "./FlightModePanel";

// SAIL color mapping
const getSailColor = (sail) => {
  const colors = {
    I: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700",
    II: "bg-lime-100 text-lime-700 border-lime-300 dark:bg-lime-900/40 dark:text-lime-300 dark:border-lime-700",
    III: "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700",
    IV: "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700",
    V: "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700",
    VI: "bg-red-200 text-red-800 border-red-400 dark:bg-red-900/60 dark:text-red-200 dark:border-red-600",
    // Own local copy of CheckMateDashboard.jsx's getSailColor (2026-09-15 hardening review K1: this
    // panel does NOT share the dashboard's helper) — needs the same OUT_OF_SCOPE branch independently.
    OUT_OF_SCOPE: "bg-rose-200 text-rose-900 border-rose-400 dark:bg-rose-900/60 dark:text-rose-200 dark:border-rose-600",
  };
  return colors[sail] || "bg-gray-100 text-gray-700 border-gray-300";
};

const isOutOfScopeSail = (sail) => (sail || "").toUpperCase() === "OUT_OF_SCOPE";

// Get operational category based on SAIL
// 2026-09-15 hardening review K1: SAIL_ORDER.indexOf("OUT_OF_SCOPE") is -1, and the original
// `sailIdx <= 1` check treated any not-found index (including OUT_OF_SCOPE) as "Open Category" —
// the mildest possible label for a mission that's actually outside SORA Specific scope entirely.
const getOperationalCategory = (sail, language = 'cs') => {
  if (isOutOfScopeSail(sail)) {
    return language === 'cs' ? 'Mimo rozsah SORA' : 'Out of SORA scope';
  }
  const SAIL_ORDER = ["I", "II", "III", "IV", "V", "VI"];
  const sailIdx = SAIL_ORDER.indexOf(sail);
  if (sailIdx <= 1) return "Open Category";
  return "Specific Category";
};

// Effort color mapping
const getEffortColor = (effort) => {
  if (effort === "low") return "text-green-600 dark:text-green-400";
  if (effort === "medium") return "text-yellow-600 dark:text-yellow-400";
  return "text-red-600 dark:text-red-400";
};

export const SoraSummaryPanel = ({
  optimizationData,
  selectedOptimizations,
  setSelectedOptimizations,
  applying,
  onApplyOptimizations,
  language = "cs",
  // WAVE 2A: Flight mode props
  flightMode = "VLOS",
  onFlightModeChange,
}) => {
  const state = optimizationData?.current_state;
  const data = optimizationData;
  const locale = language === 'cs' ? cs : en;
  const arcNoteLines = checkMateArcNotes(state, locale);
  // "Why SAIL level?" shows the first lines; the Figure 6 explanation (zones, data) can be expanded.
  const [showAllFactors, setShowAllFactors] = useState(false);
  const airspaceFactors = state?.environment_auto_detected?.airspace_factors || [];
  const visibleFactors = showAllFactors ? airspaceFactors : airspaceFactors.slice(0, 3);

  // Translations
  const t = language === 'cs' ? {
    sailLevel: "Úroveň SAIL",
    metrics: "Metriky",
    environment: "Prostředí",
    altitude: "Výška",
    equipment: "Vybavení",
    parachute: "Padák",
    fts: "FTS",
    geoAwareness: "Geo-awareness",
    remoteId: "Remote ID",
    remoteIdWarning: "Dron nemá ověřený Remote ID — zkontrolujte před letem",
    optimizations: "Optimalizace",
    alreadyOptimal: "Již optimální",
    noOptimizationsNeeded: "Pro tuto misi nejsou k dispozici další optimalizace.",
    noSimpleReductions: "Bez jednoduchého snížení rizika",
    noSimpleReductionsOutOfScope: "Mise je mimo rozsah SORA Specific — žádná jednoduchá úprava (výška, vybavení, alternativní dron) ji nevrátí do rozsahu. Vyžaduje kategorii Certified nebo zásadní přepracování mise.",
    availableOptimizations: "Dostupné optimalizace",
    applySelected: "Aplikovat vybrané",
    applying: "Aplikuji...",
    sailImprovement: "Zlepšení SAIL",
    effort: "Náročnost",
    low: "nízká",
    medium: "střední",
    high: "vysoká",
    environmentWarning: "Varování prostředí",
    whySail: "Proč tato úroveň SAIL?"
  } : {
    sailLevel: "SAIL Level",
    metrics: "Metrics",
    environment: "Environment",
    altitude: "Altitude",
    equipment: "Equipment",
    parachute: "Parachute",
    fts: "FTS",
    geoAwareness: "Geo-awareness",
    remoteId: "Remote ID",
    remoteIdWarning: "Drone does not have verified Remote ID — check before flight",
    optimizations: "Optimizations",
    alreadyOptimal: "Already Optimal",
    noOptimizationsNeeded: "No further optimizations available for this mission.",
    noSimpleReductions: "No simple risk reduction available",
    noSimpleReductionsOutOfScope: "The mission is outside SORA Specific scope — no simple change (altitude, equipment, alternative drone) brings it back into scope. Requires the Certified category or a fundamental mission redesign.",
    availableOptimizations: "Available Optimizations",
    applySelected: "Apply Selected",
    applying: "Applying...",
    sailImprovement: "SAIL Improvement",
    effort: "Effort",
    low: "low",
    medium: "medium",
    high: "high",
    environmentWarning: "Environment Warning",
    whySail: "Why this SAIL level?"
  };
  
  // Collect all available optimizations. Only suggestions that lower SAIL are selectable; the rest
  // (sail_improvement 0, e.g. M1C, M1A or fleet aircraft that cannot be compared) are informational notes.
  const { allOptimizations, informationalOptimizations } = useMemo(() => {
    const quickWins = data?.ranked?.quick_wins || [];
    const recommended = data?.ranked?.recommended || [];
    const possible = data?.ranked?.possible?.filter(o => o.type !== "environment") || [];
    const all = [...quickWins, ...recommended, ...possible];
    return {
      allOptimizations: all.filter(o => o.sail_improvement > 0),
      informationalOptimizations: all.filter(o => !(o.sail_improvement > 0)),
    };
  }, [data]);

  const hasOptimizations = allOptimizations.length > 0;
  
  // Generate a stable unique ID for an optimization
  const getOptimizationId = (opt, index = 0) => {
    if (opt.id) return opt.id;
    // Create unique ID from multiple fields to ensure uniqueness
    return `${opt.type || 'unknown'}-${opt.sail_improvement || 0}-${opt.title || ''}-${opt.effort || ''}-${index}`;
  };
  
  // Toggle optimization selection - store full optimization object
  const toggleOptimization = (opt, index) => {
    const optId = getOptimizationId(opt, index);
    setSelectedOptimizations(prev => {
      const isSelected = prev.some((o, i) => getOptimizationId(o, i) === optId);
      if (isSelected) {
        return prev.filter((o, i) => getOptimizationId(o, i) !== optId);
      } else {
        return [...prev, { ...opt, _uniqueId: optId }];
      }
    });
  };
  
  // Check if an optimization is selected
  const isOptimizationSelected = (opt, index) => {
    const optId = getOptimizationId(opt, index);
    return selectedOptimizations.some((o) => (o._uniqueId || getOptimizationId(o)) === optId);
  };
  
  // Get effort label
  const getEffortLabel = (effort) => {
    if (effort === "low") return t.low;
    if (effort === "medium") return t.medium;
    return t.high;
  };
  
  // Get localized title for optimization
  const getLocalizedTitle = (opt) => {
    if (language === 'cs' && opt.title_cs) return opt.title_cs;
    if (language === 'en' && opt.title_en) return opt.title_en;
    return opt.title || opt.type || 'Optimization';
  };

  return (
    <div className="space-y-6">
      {/* SAIL Badge + Category */}
      <div className="text-center pb-4 border-b border-[var(--border-default)]">
        <Badge className={`${getSailColor(state?.current_sail)} text-xl px-4 py-2 font-bold border-2 gap-1.5`}>
          {isOutOfScopeSail(state?.current_sail)
            ? (language === 'cs' ? 'Mimo SORA' : 'Out of SORA')
            : `SAIL ${state?.current_sail}`}
          {(state?.grc_provisional || hasArcProvisionalReason(state?.provisional_reasons))
            && hasSailWarningReason(state?.provisional_reasons) && (
            // A `title` prop on lucide's <svg> becomes an SVG attribute, not a <title> child element
            // — needs a title-bearing wrapper for a real browser tooltip.
            <span title={sailWarningTooltip(state?.provisional_reasons, language)}>
              <AlertTriangle className="h-4 w-4" />
            </span>
          )}
        </Badge>
        <p className="text-xs text-[var(--text-muted)] mt-2">
          {getOperationalCategory(state?.current_sail, language)}
        </p>
      </div>
      
      {/* WAVE 2A: Flight Mode Selector */}
      {onFlightModeChange && (
        <div className="pb-4 border-b border-[var(--border-default)]">
          <FlightModePanel
            value={flightMode}
            onChange={onFlightModeChange}
            language={language}
          />
        </div>
      )}
      
      {/* Key Metrics - 2x2 grid */}
      <div>
        <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-3">
          {t.metrics}
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-default)]">
            <div className="font-semibold text-lg text-[var(--text-primary)]">
              {/* "GRC-OUT-OF-SCOPE" (SoraGrcMethods.OutOfScopeGrc) is a wire token, not a label —
                  2026-09-16 review round 2. */}
              {(state?.current_grc || '').toUpperCase() === 'GRC-OUT-OF-SCOPE'
                ? (language === 'cs' ? 'Mimo SORA' : 'Out of SORA')
                : (state?.current_grc || '-')}
            </div>
            <div className="text-xs text-[var(--text-muted)]">GRC</div>
          </div>
          <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-default)]">
            <div className="font-semibold text-lg text-[var(--text-primary)]">{state?.current_arc || '-'}</div>
            <div className="text-xs text-[var(--text-muted)]">ARC</div>
          </div>
          <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-default)]">
            <div className="font-semibold text-lg text-[var(--text-primary)] capitalize">{state?.environment || '-'}</div>
            <div className="text-xs text-[var(--text-muted)]">{t.environment}</div>
          </div>
          <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-default)]">
            <div className="font-semibold text-lg text-[var(--text-primary)]">{state?.max_altitude_m || '-'}m</div>
            <div className="text-xs text-[var(--text-muted)]">{t.altitude}</div>
          </div>
        </div>
      </div>
      
      {/* Environment Warning */}
      {data?.environment_warning?.has_warning && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-sm text-amber-800 dark:text-amber-200">{t.environmentWarning}</p>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">{data.environment_warning.reason}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Why SAIL level? */}
      {airspaceFactors.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2">{t.whySail}</h4>
          <div className="space-y-1" data-testid="checkmate-why-sail">
            {visibleFactors.map((factor, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-[var(--text-secondary)]">
                <ArrowRight className="h-3 w-3 flex-shrink-0 mt-0.5 text-purple-500" />
                <span>{localizeArcLine(factor, language, locale, state?.provisional_reasons)}</span>
              </div>
            ))}
          </div>
          {airspaceFactors.length > 3 && (
            <button
              type="button"
              className="mt-1 text-xs text-purple-600 dark:text-purple-400 hover:underline"
              onClick={() => setShowAllFactors(!showAllFactors)}
              data-testid="checkmate-why-sail-toggle"
            >
              {showAllFactors
                ? locale.arcDisplay.showFewerLines
                : fillTemplate(locale.arcDisplay.showAllLines, { count: airspaceFactors.length })}
            </button>
          )}
        </div>
      )}

      {/* Figure 6 notes: coordination with ATS in a CTR/MCTR, TMPR of BVLOS */}
      {arcNoteLines.length > 0 && (
        <div className="space-y-1" data-testid="checkmate-arc-notes">
          {arcNoteLines.map((note, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-[var(--text-secondary)]">
              <Info className="h-3 w-3 flex-shrink-0 mt-0.5 text-purple-500" />
              <span>{note}</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Equipment */}
      <div>
        <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-3">
          {t.equipment}
        </h4>
        <div className="flex flex-wrap gap-2">
          <Badge variant={state?.current_equipment?.parachute ? "default" : "outline"} 
                 className={`text-xs ${state?.current_equipment?.parachute ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "opacity-50"}`}>
            {state?.current_equipment?.parachute ? "✓" : "✗"} {t.parachute}
          </Badge>
          <Badge variant={state?.current_equipment?.fts ? "default" : "outline"}
                 className={`text-xs ${state?.current_equipment?.fts ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "opacity-50"}`}>
            {state?.current_equipment?.fts ? "✓" : "✗"} {t.fts}
          </Badge>
          <Badge variant={state?.current_equipment?.geo_awareness ? "default" : "outline"}
                 className={`text-xs ${state?.current_equipment?.geo_awareness ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "opacity-50"}`}>
            {state?.current_equipment?.geo_awareness ? "✓" : "✗"} {t.geoAwareness}
          </Badge>
          <Badge variant={state?.current_equipment?.remote_id ? "default" : "outline"}
                 className={`text-xs ${state?.current_equipment?.remote_id ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "opacity-50"}`}>
            {state?.current_equipment?.remote_id ? "✓" : "✗"} {t.remoteId}
          </Badge>
        </div>
        
        {/* Remote ID Compliance Warning */}
        {(state?.aircraft_remote_id_compliant === false || state?.aircraft_remote_id_compliant === null || state?.aircraft_remote_id_compliant === undefined) && (
          <div className="mt-3 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                {t.remoteIdWarning}
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Optimizations Section */}
      <div className="pt-4 border-t border-[var(--border-default)]">
        <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-3">
          {t.optimizations}
        </h4>
        
        {!hasOptimizations ? (
          // 2026-09-16 review (Codex gpt-5.6-sol): a green "Already Optimal" for a mission with zero
          // simple optimizations because it's OUT_OF_SCOPE is misleading — there's nothing "optimal"
          // about a mission outside SORA Specific scope, it just has no simple fix.
          isOutOfScopeSail(state?.current_sail) ? (
            <div className="p-4 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
              <div className="flex items-center gap-2 text-rose-700 dark:text-rose-300">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">{t.noSimpleReductions}</span>
              </div>
              <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
                {t.noSimpleReductionsOutOfScope}
              </p>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                <Check className="h-5 w-5" />
                <span className="font-medium">{t.alreadyOptimal}</span>
              </div>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                {t.noOptimizationsNeeded}
              </p>
            </div>
          )
        ) : (
          <div className="space-y-3">
            {/* Optimization list with checkboxes */}
            <div className="space-y-2">
              {allOptimizations.slice(0, 6).map((opt, index) => {
                const optId = getOptimizationId(opt, index);
                const isSelected = isOptimizationSelected(opt, index);
                
                return (
                  <div 
                    key={optId}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                        : 'border-[var(--border-default)] bg-[var(--bg-elevated)] hover:border-purple-300'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOptimization(opt, index);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={(e) => {
                          if (e && e.stopPropagation) e.stopPropagation();
                          toggleOptimization(opt, index);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                          {getLocalizedTitle(opt)}
                        </p>
                        {/* 2026-09-16 review (Codex gpt-5.6-sol): opt.description was never
                            rendered here at all — a candidate/current calculation-method mismatch
                            disclosure (SoraOptimizationEngine's altReasoningSuffix) silently never
                            reached the user, so an apparent SAIL improvement from incomparable
                            methods showed with no warning. */}
                        {opt.description && (
                          <p className="text-xs text-[var(--text-muted)] mt-0.5 line-clamp-2">
                            {opt.description}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs">
                          <span className="text-purple-600 dark:text-purple-400">
                            -{opt.sail_improvement} SAIL
                          </span>
                          <span className={getEffortColor(opt.effort)}>
                            {t.effort}: {getEffortLabel(opt.effort)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Apply button */}
            {selectedOptimizations.length > 0 && (
              <Button 
                onClick={onApplyOptimizations}
                disabled={applying}
                className="w-full gap-2 bg-[#1E3A5F] hover:bg-[#2a4a73]"
              >
                {applying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.applying}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    {t.applySelected} ({selectedOptimizations.length})
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        {/* Informational suggestions: nothing to select or apply */}
        {informationalOptimizations.length > 0 && (
          <div className="space-y-2 mt-3" data-testid="checkmate-optimization-notes">
            {informationalOptimizations.map((opt, index) => (
              <div
                key={`${opt.type || 'unknown'}-${opt.title || ''}-${index}`}
                className="flex items-start gap-2 p-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-elevated)]"
              >
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-[var(--text-muted)]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-primary)]">
                    {getLocalizedTitle(opt)}
                  </p>
                  {opt.description && (
                    <p className="text-xs text-[var(--text-muted)] mt-0.5">
                      {opt.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SoraSummaryPanel;
